package com.tovtalk.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.defaultMinSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.ui.draw.clip
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.NavigationBar
import androidx.compose.material3.NavigationBarItem
import androidx.compose.material3.NavigationBarItemDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.Alignment
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.foundation.layout.size
import com.tovtalk.ui.theme.TovTalkTheme

private val NavIconSize = 28.dp

/**
 * Main bottom navigation bar – compact height, icons only.
 * Data-driven: renders items from the [items] list, consistent with NavigationRail.
 * CTA items (isCtaButton=true) get a branded gradient background.
 * Badge count is shown from NavigationItem.badgeCount.
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun MainBottomBar(
    items: List<NavigationItem>,
    selectedRoute: String,
    onItemClick: (NavigationItem) -> Unit,
    modifier: Modifier = Modifier,
    bottomPadding: Dp = 0.dp,
) {
    val barHeight = TovTalkTheme.spacing.bottomNavHeight
    Column(
        modifier = modifier.fillMaxWidth()
    ) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(barHeight + bottomPadding)
                .background(TovTalkTheme.colors.backgroundSecondary)
        ) {
            Box(
                modifier = Modifier
                    .align(Alignment.TopCenter)
                    .fillMaxWidth()
            ) {
                val navItemColors = NavigationBarItemDefaults.colors(
                    indicatorColor = Color.Transparent,
                    selectedIconColor = TovTalkTheme.colors.textPrimary,
                    unselectedIconColor = TovTalkTheme.colors.textSecondary
                )
                NavigationBar(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(barHeight),
                    containerColor = Color.Transparent,
                    contentColor = TovTalkTheme.colors.textPrimary,
                    tonalElevation = TovTalkTheme.elevation.none,
                    windowInsets = WindowInsets(0, 0, 0, 0)
                ) {
                    items.forEach { item ->
                        val isSelected = item.route == selectedRoute
                        NavigationBarItem(
                            icon = {
                                if (item.isCtaButton) {
                                    // Upload / CTA button with gradient
                                    Box(
                                        modifier = Modifier
                                            .clip(TovTalkTheme.shapes.small)
                                            .background(
                                                brush = Brush.horizontalGradient(
                                                    colors = listOf(
                                                        TovTalkTheme.colors.brandLight,
                                                        TovTalkTheme.colors.brand
                                                    )
                                                )
                                            )
                                            .padding(horizontal = TovTalkTheme.spacing.sm, vertical = TovTalkTheme.spacing.xxxs),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        Icon(
                                            imageVector = item.icon,
                                            contentDescription = item.label,
                                            modifier = Modifier.size(NavIconSize),
                                            tint = TovTalkTheme.colors.onBrand
                                        )
                                    }
                                } else {
                                    // Regular icon with optional badge
                                    Box {
                                        Icon(
                                            imageVector = if (isSelected) item.selectedIcon else item.icon,
                                            contentDescription = item.label,
                                            modifier = Modifier.size(NavIconSize),
                                            tint = if (isSelected) TovTalkTheme.colors.textPrimary else TovTalkTheme.colors.textSecondary
                                        )
                                        if (item.badgeCount > 0) {
                                            Box(
                                                modifier = Modifier
                                                    .align(Alignment.TopEnd)
                                                    .offset(x = 8.dp, y = (-6).dp)
                                                    .defaultMinSize(minWidth = 18.dp, minHeight = 18.dp)
                                                    .clip(CircleShape)
                                                    .background(TovTalkTheme.colors.brand)
                                                    .padding(horizontal = 2.dp),
                                                contentAlignment = Alignment.Center
                                            ) {
                                                Text(
                                                    text = if (item.badgeCount > 99) "99+" else item.badgeCount.toString(),
                                                    style = TovTalkTheme.typography.labelSmall,
                                                    color = TovTalkTheme.colors.onBrand
                                                )
                                            }
                                        }
                                    }
                                }
                            },
                            label = {},
                            selected = isSelected,
                            onClick = { onItemClick(item) },
                            colors = navItemColors
                        )
                    }
                }
            }
        }
    }
}
